/**
 * Admin Stream Continuity Page
 *
 * Tools for detecting and recovering from WebSocket capture gaps:
 * - Capture gap analysis (detects disruptions where transactions may have been lost)
 * - Range recovery (reprocesses ledgers to pick up missed transactions)
 * - Single ledger recovery
 *
 * Note: "capture gaps" here are timestamp discontinuities in ledger_versions —
 * the ledger rows exist, but the stream stalled while they were written. Missing
 * ledger rows are a different problem, handled on the Ledger Health page.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

// ==================== API ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function postWithAuth(endpoint: string, body?: unknown) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface CaptureGap {
  gap_start_ledger: number;
  gap_end_ledger: number;
  gap_start_time: string;
  gap_end_time: string;
  gap_seconds: number;
  estimatedEndLedger: number;
  ledgersInRange: number;
  ledgersRecovered: number;
  capturedNftTxs: number;
  capturedAccepts: number;
  unenrichedAccepts: number;
}

interface RecoveryStatus {
  isRecovering: boolean;
  currentLedger?: number;
  totalLedgers?: number;
  processed?: number;
  failed?: number;
}

// ==================== Helpers ====================

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function severityStyles(gapSeconds: number): { text: string; bg: string; border: string } {
  if (gapSeconds >= 120) return { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
  if (gapSeconds >= 60) return { text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' };
  return { text: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200' };
}

// ==================== Component ====================

export function AdminLedgerRecoveryPage() {
  const [gaps, setGaps] = useState<CaptureGap[]>([]);
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Recovery form state
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [singleLedger, setSingleLedger] = useState('');

  // Action state
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Gap analysis params
  const [hours, setHours] = useState('24');
  const [threshold, setThreshold] = useState('60');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [gapsRes, statusRes] = await Promise.all([
        fetchWithAuth(`/admin/ledger-health/capture-gaps?hours=${hours}&thresholdSeconds=${threshold}`),
        fetchWithAuth('/admin/ledger-health/recovery-status').catch(() => ({ data: null })),
      ]);

      if (gapsRes.success) setGaps(gapsRes.data.gaps);
      if (statusRes.data) setRecoveryStatus(statusRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [hours, threshold]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRecoverRange = async (startIndex: number, endIndex: number) => {
    const rangeSize = endIndex - startIndex + 1;
    const key = `recover-${startIndex}-${endIndex}`;
    setActionLoading(key);
    setActionResult(null);
    try {
      const result = await postWithAuth('/admin/ledger-health/recover-range', {
        startIndex,
        endIndex,
        concurrency: 1,
        delayMs: 500,
      });
      setActionResult({
        type: 'success',
        message: result.data?.message || `Recovery started for ${rangeSize} ledgers (${startIndex.toLocaleString()} - ${endIndex.toLocaleString()})`,
      });
      setTimeout(loadData, 3000);
    } catch (err: any) {
      setActionResult({ type: 'error', message: err.message || 'Recovery failed' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRecoverSingle = () => {
    const idx = parseInt(singleLedger, 10);
    if (!idx || idx <= 0) {
      setActionResult({ type: 'error', message: 'Enter a valid ledger index' });
      return;
    }
    handleRecoverRange(idx, idx);
  };

  const handleRecoverRangeForm = () => {
    const start = parseInt(rangeStart, 10);
    const end = parseInt(rangeEnd, 10);
    if (!start || !end || start <= 0 || end <= 0) {
      setActionResult({ type: 'error', message: 'Enter valid start and end ledger indices' });
      return;
    }
    if (start > end) {
      setActionResult({ type: 'error', message: 'Start index must be less than or equal to end index' });
      return;
    }
    if (end - start > 10000) {
      setActionResult({ type: 'error', message: 'Range too large (max 10,000 ledgers per request)' });
      return;
    }
    handleRecoverRange(start, end);
  };

  const handleRecoverGap = (gap: CaptureGap) => {
    handleRecoverRange(gap.gap_start_ledger, gap.estimatedEndLedger);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stream Continuity</h1>
            <p className="mt-1 text-sm text-gray-600">
              Detects periods where the WebSocket stalled, so ledgers look "completed"
              but transactions inside them may be missing. For missing ledgers entirely,
              use <Link to="/admin/ledger-health" className="text-blue-600 hover:underline">Ledger Health</Link>.
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {error && <ErrorDisplay error={error} onRetry={loadData} />}

        {/* Action Result Banner */}
        {actionResult && (
          <div className={`rounded-lg border p-3 text-sm ${
            actionResult.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {actionResult.message}
            <button onClick={() => setActionResult(null)} className="ml-2 underline">dismiss</button>
          </div>
        )}

        {/* Recovery Status */}
        {recoveryStatus?.isRecovering && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <div className="flex items-center gap-2 text-indigo-700 text-sm font-medium">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-r-transparent" />
              Recovery in progress
            </div>
            {recoveryStatus.processed !== undefined && (
              <p className="text-xs text-indigo-600 mt-1">
                Processed {recoveryStatus.processed} / {recoveryStatus.totalLedgers} ledgers
                {recoveryStatus.failed ? `, ${recoveryStatus.failed} failed` : ''}
              </p>
            )}
          </div>
        )}

        {/* Recovery Tools */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Single Ledger Recovery */}
          <div className="bg-white shadow-sm rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Recover Single Ledger</h3>
            <div className="flex gap-2">
              <input
                type="number"
                value={singleLedger}
                onChange={(e) => setSingleLedger(e.target.value)}
                placeholder="Ledger index"
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleRecoverSingle}
                disabled={actionLoading !== null}
                className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Recover
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Re-fetches the ledger from XRPL and processes all NFT transactions. Idempotent.
            </p>
          </div>

          {/* Range Recovery */}
          <div className="bg-white shadow-sm rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Recover Range</h3>
            <div className="flex gap-2">
              <input
                type="number"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                placeholder="Start ledger"
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="number"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                placeholder="End ledger"
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleRecoverRangeForm}
                disabled={actionLoading !== null}
                className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Recover
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Reprocesses all ledgers in range. Max 10,000 per request.
            </p>
          </div>
        </div>

        {/* Capture Gap Analysis */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Capture Gap Analysis</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Periods where the WebSocket stream stalled. Ledgers show "completed" but may have missing transactions.
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs shrink-0 ml-4">
              <label className="flex items-center gap-1.5">
                <span className="text-gray-500">Hours:</span>
                <select
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="6">6h</option>
                  <option value="12">12h</option>
                  <option value="24">24h</option>
                  <option value="48">48h</option>
                  <option value="168">7d</option>
                </select>
              </label>
              <label className="flex items-center gap-1.5">
                <span className="text-gray-500">Threshold:</span>
                <select
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="10">10s</option>
                  <option value="20">20s</option>
                  <option value="30">30s</option>
                  <option value="60">60s</option>
                </select>
              </label>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
                <p className="mt-4 text-sm text-gray-600">Analyzing capture gaps...</p>
              </div>
            </div>
          ) : gaps.length === 0 ? (
            <div className="px-4 py-3 border-b border-gray-200 bg-green-50">
              <p className="text-sm text-green-800 font-medium">
                No capture gaps detected in the last {hours} hours (threshold: {threshold}s)
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Affected Range</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sales</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Missed Sales</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {gaps.map((gap) => {
                    const styles = severityStyles(gap.gap_seconds);
                    const recoveryRange = gap.estimatedEndLedger - gap.gap_start_ledger + 1;
                    const actionKey = `recover-${gap.gap_start_ledger}-${gap.estimatedEndLedger}`;
                    const isRecovered = gap.ledgersRecovered > 0;
                    return (
                      <tr key={gap.gap_start_ledger} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className={`text-sm font-bold font-mono ${styles.text}`}>
                            {formatDuration(gap.gap_seconds)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            ~{recoveryRange.toLocaleString()} ledgers
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            {gap.gap_start_ledger.toLocaleString()} &ndash; {gap.estimatedEndLedger.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-500">
                            {formatDate(gap.gap_start_time)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDate(gap.gap_end_time)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <span className={gap.capturedAccepts > 0 ? 'text-green-700 font-medium' : 'text-gray-400'}>
                            {gap.capturedAccepts}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          {gap.unenrichedAccepts > 0 ? (
                            <span className="text-red-700 font-medium">{gap.unenrichedAccepts}</span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isRecovered ? (
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                              Recovered
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                              Unknown
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleRecoverGap(gap)}
                            disabled={actionLoading !== null}
                            className="rounded bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 disabled:opacity-50"
                          >
                            {actionLoading === actionKey ? 'Starting...' : 'Recover'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* How It Works */}
        <details className="bg-white shadow-sm rounded-lg overflow-hidden">
          <summary className="px-4 py-3 text-sm font-semibold text-gray-900 cursor-pointer select-none hover:bg-gray-50 border-b border-gray-200">
            How Recovery Works
          </summary>
          <div className="px-4 py-3">
            <ul className="text-xs text-gray-600 space-y-1.5 list-disc list-inside">
              <li>Recovery fetches full ledger data from XRPL and reprocesses all NFT transactions</li>
              <li>Already-processed transactions are safely skipped (idempotent via ON CONFLICT)</li>
              <li>New transactions get full processing: ownership updates, offer changes, sale records, stats</li>
              <li>Duplicate notifications are suppressed (skipNotifications: true for re-processed txs)</li>
              <li>
                <strong className="text-gray-700">What are capture gaps?</strong> Periods where the WebSocket
                transaction stream stalled (e.g., during a brief disconnection). Ledgers validated during the
                gap are marked "completed" because some transactions arrived in a catch-up burst after reconnection,
                but individual transactions may have been lost. Recovery re-fetches from XRPL to fill in what was missed.
              </li>
            </ul>
          </div>
        </details>
      </div>
    </AdminLayout>
  );
}
